import { cn } from "@win-reality/react-ui";
import { Minus, Plus } from "lucide-react";
import { ReactNode } from "react";

/** Labelled rows and steppers for a play's results (runs, outs, runners). */

export const ResultRow = ({
  label,
  isAuto = false,
  badgeTestId,
  badgeField,
  children,
}: {
  label: string;
  /** Marks a value filled in from the play result that hasn't been edited */
  isAuto?: boolean;
  /** Test hooks on the AUTO badge: literal id and its `data-field` */
  badgeTestId?: string;
  badgeField?: string;
  children: ReactNode;
}) => (
  <div
    className={cn(
      // Size
      "min-h-14 w-full",
      // Layout
      "flex items-center justify-between gap-4",
      // Spacing
      "px-4 py-2",
      // Border
      "rounded-md border border-tagging-option-border",
      // Background
      "bg-tagging-option",
      // Effects
      "shadow-[0_0_4px_0_rgba(0,0,0,0.25)]",
    )}
  >
    <span className="flex items-center gap-2">
      <span className="text-sm font-semibold uppercase text-gray-900 dark:text-white">
        {label}
      </span>
      {isAuto && (
        <AutoBadge data-testid={badgeTestId} data-field={badgeField} />
      )}
    </span>
    {children}
  </div>
);

/** Marks a value the tracker or a rule filled in, not the scorekeeper. */
export const AutoBadge = ({
  "data-testid": testId,
  "data-field": field,
}: {
  "data-testid"?: string;
  "data-field"?: string;
}) => (
  <span
    data-testid={testId}
    data-field={field}
    className={cn(
      // Spacing
      "px-1.5 py-0.5",
      // Border
      "rounded-[4px]",
      // Background
      "bg-tagging-hotkey",
      // Text
      "whitespace-nowrap text-[10px] font-semibold uppercase text-tagging-muted",
    )}
  >
    Auto Detected
  </span>
);

/** − value + control; `children` is the displayed value. */
export const StepperControl = ({
  canDecrease,
  canIncrease,
  onDecrease,
  onIncrease,
  decrementTestId,
  incrementTestId,
  children,
}: {
  canDecrease: boolean;
  canIncrease: boolean;
  onDecrease: () => void;
  onIncrease: () => void;
  /** Test hooks: literal ids for the − and + buttons */
  decrementTestId?: string;
  incrementTestId?: string;
  children: ReactNode;
}) => (
  <div className="flex items-center gap-4">
    <button
      type="button"
      data-testid={decrementTestId}
      aria-label="Decrease"
      disabled={!canDecrease}
      onClick={onDecrease}
      className={cn(
        // Size
        "h-7 w-7",
        // Layout
        "flex items-center justify-center",
        // Border
        "rounded-full",
        // Background
        "bg-tagging-hotkey",
        // Text
        "text-gray-700 dark:text-gray-300",
        // States
        "disabled:opacity-50",
      )}
    >
      <Minus className="h-3.5 w-3.5" />
    </button>
    {children}
    <button
      type="button"
      data-testid={incrementTestId}
      aria-label="Increase"
      disabled={!canIncrease}
      onClick={onIncrease}
      className={cn(
        // Size
        "h-7 w-7",
        // Layout
        "flex items-center justify-center",
        // Border
        "rounded-full",
        // Background
        "bg-black dark:bg-white",
        // Text
        "text-white dark:text-black",
        // States
        "disabled:opacity-50",
      )}
    >
      <Plus className="h-3.5 w-3.5" />
    </button>
  </div>
);

export const Stepper = ({
  value,
  max,
  disabled = false,
  onChange,
  valueTestId,
  incrementTestId,
  decrementTestId,
}: {
  value: number;
  max: number;
  disabled?: boolean;
  onChange: (value: number) => void;
  /** Test hooks: literal ids for the value and the − / + buttons */
  valueTestId?: string;
  incrementTestId?: string;
  decrementTestId?: string;
}) => (
  <StepperControl
    canDecrease={!disabled && value > 0}
    canIncrease={!disabled && value < max}
    onDecrease={() => onChange(value - 1)}
    onIncrease={() => onChange(value + 1)}
    decrementTestId={decrementTestId}
    incrementTestId={incrementTestId}
  >
    <span
      data-testid={valueTestId}
      data-value={value}
      className="w-4 text-center text-lg font-bold text-gray-900 dark:text-white"
    >
      {value}
    </span>
  </StepperControl>
);
